/**
 * Data transformation utilities for Analytics component
 */

import { ChartDataset, ChartColorConfig } from '../_interfaces/analytics.interfaces';
import { ANALYTICS_COLORS } from '../_constants/analytics.constants';
import { lightenColor } from './analytics-formatters.util';

/**
 * Capitalize recipe name (capitalize first letter of each word)
 */
export function capitalizeRecipeName(name: string): string {
  if (!name) {
    return name;
  }
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Transform breakdown data to show top 5 + "Other"
 * Groups all items beyond the top 5 into an "Other" category
 */
export function transformToTop5PlusOther(
  labels: string[],
  data: number[]
): { labels: string[]; data: number[] } {
  if (labels.length === 0 || data.length === 0) {
    return { labels: [], data: [] };
  }

  // Create array of {label, value, index} and sort by value descending
  const items = labels.map((label, index) => ({
    label,
    value: data[index] || 0,
    index
  })).sort((a, b) => b.value - a.value);

  // Take top 5
  const top5 = items.slice(0, 5);
  const remaining = items.slice(5);

  // Calculate "Other" total
  const otherTotal = remaining.reduce((sum, item) => sum + item.value, 0);

  // Build result arrays
  const resultLabels = top5.map(item => item.label);
  const resultData = top5.map(item => item.value);

  // Add "Other" if there are remaining items
  if (remaining.length > 0 && otherTotal > 0) {
    resultLabels.push('Other');
    resultData.push(otherTotal);
  }

  return {
    labels: resultLabels,
    data: resultData
  };
}

/**
 * Create concentric circle datasets for donut chart comparison
 * Outer ring (current period) and inner ring (previous period)
 * Returns both datasets and the aligned labels
 */
export function createConcentricDonutDatasets(
  currentLabels: string[],
  currentData: number[],
  compareLabels: string[],
  compareData: number[],
  colors: string[]
): { labels: string[]; datasets: ChartDataset[] } {
  // Transform both to top 5 + Other
  const currentTransformed = transformToTop5PlusOther(currentLabels, currentData);
  const compareTransformed = transformToTop5PlusOther(compareLabels, compareData);

  // Ensure both have the same labels (union of both label sets)
  const allLabelsSet = new Set<string>();
  currentTransformed.labels.forEach(label => allLabelsSet.add(label));
  compareTransformed.labels.forEach(label => allLabelsSet.add(label));
  const allLabels = Array.from(allLabelsSet);

  // Create data arrays with matching labels, filling missing with 0
  const currentDataAligned = allLabels.map(label => {
    const index = currentTransformed.labels.indexOf(label);
    return index >= 0 ? currentTransformed.data[index] : 0;
  });

  const compareDataAligned = allLabels.map(label => {
    const index = compareTransformed.labels.indexOf(label);
    return index >= 0 ? compareTransformed.data[index] : 0;
  });

  // Create color arrays for each dataset
  const currentColors = allLabels.map((_, index) => {
    return colors[index % colors.length];
  });

  // For compare data, use lighter/desaturated versions of the same colors
  const compareColors = allLabels.map((_, index) => {
    const baseColor = colors[index % colors.length];
    // Convert hex to rgba and make it lighter
    return lightenColor(baseColor, 0.4);
  });

  return {
    labels: allLabels,
    datasets: [
      {
        label: 'Current',
        data: currentDataAligned,
        backgroundColor: currentColors,
        borderColor: '#ffffff',
        borderWidth: 2,
        weight: 1 // Outer ring
      },
      {
        label: 'Previous',
        data: compareDataAligned,
        backgroundColor: compareColors,
        borderColor: '#ffffff',
        borderWidth: 2,
        weight: 0.6 // Inner ring (smaller)
      }
    ]
  };
}

/**
 * Create single donut dataset (no comparison) with top 5 + Other
 */
export function createSingleDonutDataset(
  labels: string[],
  data: number[],
  colors: string[]
): { labels: string[]; datasets: ChartDataset[] } {
  const transformed = transformToTop5PlusOther(labels, data);

  const datasetColors = transformed.labels.map((_, index) => {
    return colors[index % colors.length];
  });

  return {
    labels: transformed.labels,
    datasets: [{
      label: 'Drink Volume',
      data: transformed.data,
      backgroundColor: datasetColors,
      borderColor: '#ffffff',
      borderWidth: 2
    }]
  };
}

